/* What the lesson's hand points at, and nothing else.

   Basic training runs the real game view, and the coach's sentence names the control to press —
   "open your city at 60-47", "press END TURN". A player who has never seen the interface still
   has to find that control among forty others, so a hand points at it (owner's ask, 2026-09-25:
   "arrows and or hand moving and pointing in the tutorial steps").

   This module is the *decision*: given where the lesson is and what is on screen, which one
   thing is the next press. It is a pure function so the whole ladder — six steps, each a chain of
   two to five presses — can be read and tested as a table, and `components/TutorialPointer` is
   left with the only part that needs a browser: finding the element and drawing over it.

   What it reads, and why each is an input rather than looked up here:

   - the run (`step`, `clear`, `acted`) from `tutorial/server` — a step that opened over sheets
     points at their × and nothing else, because the lesson answers no move until they are closed;
   - the ui slice — the selected cell, whether a march is being drafted, its nodes and troops —
     because the game view keeps its own state there and the hand has to follow the player
     through the real flow, not a scripted one;
   - the modal stack (top last), because a control inside a sheet is only pressable while the
     sheet is up, and a sheet that is not part of the lesson is something to close first;
   - a `probe` over the document — `exists(selector)` and `value(selector)` — for the two things
     the store does not hold: a picker that is component state (the origin list), and what a
     number field currently says. A probe rather than `document`, so a test can answer for it.

   A target is `{kind: "cell", at: "60-47"}` — a square on the map — or `{kind: "element",
   selector}`, or null when there is nothing to point at (the battle view is open and is being
   read). The selectors are `data-*` hooks on the real controls, listed once in `SELECTORS` so a
   renamed class cannot quietly point the hand at nothing. */

import { DEFENDERS_WANTED, HORDE_AT, LEARNER_AT } from "./scenario"

export const SELECTORS = {
  // The round.
  endTurn: '[data-action="end-turn"]',
  // The map's action bar, and the origin picker "march here" opens inside it. Every order the lesson
  // asks for is pointed at here, the city sheet open or not (owner, 2026-09-30): the sheet ends
  // above the bar on every shell, so the bar is always in sight.
  city: '.map-action-bar [data-action="city"]',
  march: '.map-action-bar [data-action="march"]',
  marchTo: '.map-action-bar [data-action="marchTo"]',
  settle: '.map-action-bar [data-action="settle"]',
  battle: '.map-action-bar [data-action="battle"]',
  origin: '.map-action-bar [data-action="origin"]',
  // The city sheet: what the city is and makes — its buildings and its production queue.
  upgradeCard: (building) => `[data-building="${building}"] .building-cost`,
  upgradeInSheet: (building) => `[data-action="upgrade"][data-building="${building}"]`,
  soldiers: '.production-unit-number[data-unit="soldier"]',
  setQueue: '[data-action="set-queue"]',
  // The battle's doors.
  battleLine: '.round-event-click[data-event^="battle"]',
  reportTab: '[data-tab="report"]',
  continue: ".coach-continue",
  // The march: the force in the modal, the road on the map, the send in either.
  allIn: '[data-action="all-in"]',
  unitAll: (unit) => `.march-unit-all[data-unit="${unit}"]`,
  drawRoute: '[data-action="draw-route"]',
  sendMarch: '[data-action="send-march"]',
  chooseTroops: '[data-action="choose-troops"]',
  sendRoute: '[data-action="send-route"]',
  // The way out of a sheet that is not on the lesson's road.
  dismiss: ".modal-layer:not(.modal-layer-hidden) .modal-dismiss",
  // The phone's bottom sheets (the round report, the forces) are not modals, so `dismiss` does not
  // find them; the renderer falls back to this when a pointed-at control lies under one. (With the
  // stack empty and the step not yet clear, `dismiss` finds nothing and the hand simply hides for
  // the settle `views/Tutorial` waits.)
  closeSheet: ".sheet-layer .sheet-close",
}

/* The sheets the lesson walks through. Anything else on top — settings, research, the shop, a
   unit's stats — is a detour, and the hand points at its close button rather than at a control
   underneath it that cannot be pressed. */
const LESSON_MODALS = new Set(["CityModal", "BuildingModal", "AttackModal", "BattleModal"])

const element = (selector) => ({ kind: "element", selector })
// A field the lesson wants a number typed into: the hand carries the number (owner, 2026-10-07),
// so "train sixty soldiers" is not a sentence to go back and read with the field already focused.
const numberField = (selector, count) => ({ kind: "element", selector, count })
const cell = (at) => ({ kind: "cell", at })

const isAt = (selected, at) => selected != null && `${selected.col}-${selected.row}` === at

const count = (troops, unit) => Number(troops?.[unit] || 0)
const total = (troops) => Object.values(troops || {}).reduce((sum, number) => sum + (Number(number) || 0), 0)

/** The next press. See the module note for the shape of every argument. */
export const pointerTargetFor = ({ step, clear = true, acted, ui = {}, modals = [], founders = 0, probe }) => {
  if (!step) return null
  const top = modals.length ? modals[modals.length - 1] : null
  // A step opens on a clear screen (owner, 2026-10-03): the sheets the last step left open are
  // closed first, and the renderer finds the top one's × (or the phone sheet's) for this selector.
  // The battle step is the exception, because its move is reading a sheet: the report open is the
  // clear screen (`server.battleOpened`).
  if (!clear) {
    if (step === "battle" && top === "BattleModal") return null
    return element(SELECTORS.dismiss)
  }
  const selected = ui.selectedCity ?? null
  const exists = (selector) => Boolean(probe?.exists?.(selector))

  if (top && !LESSON_MODALS.has(top)) return element(SELECTORS.dismiss)

  // The move is made: the round turns on END TURN — or, for the one step that turns no round, on
  // the coach's own Continue. A sheet still covering END TURN is the renderer's problem: it
  // checks the button can be reached and falls back to the sheet's close when it cannot.
  if (acted) return element(step === "battle" ? SELECTORS.continue : SELECTORS.endTurn)

  switch (step) {
    case "commandCenter":
    case "cultivationCenter": {
      const building = step === "commandCenter" ? "command_center" : "cultivation_center"
      if (top === "BuildingModal") {
        // The sheet of the right building has the upgrade; the sheet of any other is a detour.
        const upgrade = SELECTORS.upgradeInSheet(building)
        return element(exists(upgrade) ? upgrade : SELECTORS.dismiss)
      }
      if (top === "CityModal") return element(SELECTORS.upgradeCard(building))
      if (top) return element(SELECTORS.dismiss)
      if (isAt(selected, LEARNER_AT)) return element(SELECTORS.city)
      return cell(LEARNER_AT)
    }

    case "defend": {
      if (top === "CityModal") {
        // The field says the number the coach asked for: the press is the order. Otherwise the
        // field itself, whatever it says — sixty is what the server will accept.
        const typed = probe?.value?.(SELECTORS.soldiers)
        return Number(typed) === DEFENDERS_WANTED
          ? element(SELECTORS.setQueue)
          : numberField(SELECTORS.soldiers, DEFENDERS_WANTED)
      }
      if (top) return element(SELECTORS.dismiss)
      if (isAt(selected, LEARNER_AT)) return element(SELECTORS.city)
      return cell(LEARNER_AT)
    }

    case "battle": {
      // The report is opened from whichever door the player finds (the run counts any of them).
      if (top === "BattleModal") return null
      // The doors are the round's report and the bar's BATTLE, and the city sheet leaves both in
      // sight; any other sheet is closed first.
      if (top && top !== "CityModal") return element(SELECTORS.dismiss)
      if (exists(SELECTORS.battleLine)) return element(SELECTORS.battleLine)
      // The phone keeps the report behind a tab.
      if (exists(SELECTORS.reportTab)) return element(SELECTORS.reportTab)
      if (exists(SELECTORS.battle)) return element(SELECTORS.battle)
      if (top) return element(SELECTORS.dismiss)
      return isAt(selected, LEARNER_AT) ? null : cell(LEARNER_AT)
    }

    case "attack": {
      const troops = ui.movementTroops || {}
      const nodes = ui.selectedNodes || []
      if (ui.creatingMovement) {
        if (top === "AttackModal") {
          // The force first (owner's Q2): the whole city is the shortest answer, then the workers
          // the colony will be paid with if the player picked by hand, then the road, then the send.
          if (total(troops) === 0) return element(SELECTORS.allIn)
          if (count(troops, "worker") < founders) return element(SELECTORS.unitAll("worker"))
          if (nodes.length < 2) return element(SELECTORS.drawRoute)
          return element(SELECTORS.sendMarch)
        }
        if (top) return element(SELECTORS.dismiss)
        if (nodes.length < 2) return cell(HORDE_AT)
        if (total(troops) === 0 || count(troops, "worker") < founders) return element(SELECTORS.chooseTroops)
        return element(SELECTORS.sendRoute)
      }
      // The march is the bar's, and the city sheet leaves the bar in sight.
      if (top && top !== "CityModal") return element(SELECTORS.dismiss)
      // "March here" asks where from: the picker lists the one city that can answer.
      if (exists(SELECTORS.origin)) return element(SELECTORS.origin)
      if (isAt(selected, HORDE_AT)) return element(SELECTORS.marchTo)
      if (isAt(selected, LEARNER_AT)) return element(SELECTORS.march)
      // The camp is on the map, and a sheet may be over it.
      return top ? element(SELECTORS.dismiss) : cell(HORDE_AT)
    }

    case "settle": {
      // Settle is the bar's, sheet open or not.
      if (top === "CityModal") {
        return element(isAt(selected, HORDE_AT) ? SELECTORS.settle : SELECTORS.dismiss)
      }
      if (top) return element(SELECTORS.dismiss)
      if (isAt(selected, HORDE_AT)) return element(SELECTORS.settle)
      return cell(HORDE_AT)
    }

    default:
      return null
  }
}
